import { App } from '@capacitor/app';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
// The manifest contract, shared with scripts/ota-release.mjs so the two cannot drift.
import { DEFAULT_CHANNEL, getManifestSigningInput, getPemBody, isChannelName, isManifestValid } from '../shared/ota-manifest.js';

// Bundles that failed to boot are rolled back natively. We persist their ids so
// the app does not re-download the very same broken bundle on every launch.
const BLOCKED_BUNDLES_KEY = 'ota-blocked-bundles';

// Testers opt into a separate manifest deployed next to the production one. The
// point of doing it this way — rather than a separate TestFlight build — is that
// they stay on the very same App Store binary as everyone else.
const CHANNEL_KEY = 'ota-channel';

// The native version the running bundle was last seen alongside. See
// resetOnNativeUpgrade().
const NATIVE_VERSION_KEY = 'ota-native-version';

const base64ToBytes = (value) => Uint8Array.from(window.atob(value), (char) => char.charCodeAt(0));

// Takes the key rather than reading runtimeConfig so the verification itself can
// be unit tested against a throwaway key pair, without a Nuxt runtime.
const verifyManifestSignature = async(manifest, pem) => {
  if (!pem) {
    console.error('[OTA] No public key configured, refusing the manifest');
    return false;
  }

  try {
    const key = await window.crypto.subtle.importKey(
      'spki',
      base64ToBytes(getPemBody(pem)),
      { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
      false,
      ['verify'],
    );

    return await window.crypto.subtle.verify(
      'RSASSA-PKCS1-v1_5',
      key,
      base64ToBytes(manifest.metaSignature),
      new TextEncoder().encode(getManifestSigningInput(manifest)),
    );
  } catch (error) {
    // Fail closed: a manifest we cannot verify is one we do not act on.
    console.error('[OTA] Could not verify the manifest signature', error);
    return false;
  }
};

const getBlockedBundleIds = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(BLOCKED_BUNDLES_KEY));
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};

const blockBundleId = (bundleId) => {
  if (!bundleId) {
    return;
  }

  const blocked = getBlockedBundleIds();

  if (!blocked.includes(bundleId)) {
    try {
      // Keep the list bounded; only recent failures are worth remembering.
      window.localStorage.setItem(BLOCKED_BUNDLES_KEY, JSON.stringify([...blocked, bundleId].slice(-20)));
    } catch (error) {
      // This runs from markReady() right after a rollback, and that promise is
      // awaited before the component sets anything up — letting a full-storage
      // WKWebView throw here would take the whole update check down with it.
      console.error('[OTA] Could not persist the blocked bundle', error);
    }
  }
};

// Checked on the way out too, not just on the way in: localStorage is the one
// input here that another script on the origin could have written.
const getChannel = () => {
  try {
    const stored = window.localStorage.getItem(CHANNEL_KEY);
    return isChannelName(stored) ? stored : DEFAULT_CHANNEL;
  } catch {
    return DEFAULT_CHANNEL;
  }
};

const setChannel = (channel) => {
  try {
    window.localStorage.setItem(CHANNEL_KEY, isChannelName(channel) ? channel : DEFAULT_CHANNEL);
    return true;
  } catch (error) {
    console.error('[OTA] Could not persist the channel', error);
    return false;
  }
};

// `…/ota-manifest.json` → `…/ota-manifest-beta.json`. Kept pure so the mapping
// is unit tested; an unknown channel must never silently fall back to
// production, or a tester would think they are on beta when they are not.
const getManifestUrl = (baseUrl, channel) => (
  !channel || channel === DEFAULT_CHANNEL
    ? baseUrl
    : baseUrl.replace(/\.json(\?.*)?$/, `-${channel}.json$1`)
);

// `OTA_MANIFEST_URL` may carry a query of its own — the channel mapping above
// already accounts for one — so the separator has to be picked, not assumed.
const withCacheBuster = (url, timestamp) => `${url}${url.includes('?') ? '&' : '?'}t=${timestamp}`;

const fetchManifest = async() => {
  // Served from the live site (not from the locally bundled copy) — on iOS the
  // local bundle lives on the `capacitor://` origin, so an `https://` request
  // genuinely hits the network. `nuxt.config.js` defaults this to production and
  // lets `OTA_MANIFEST_URL` aim a TestFlight build at a preview deployment.
  //
  // Deliberately plain `$fetch`, not `useApi` — the manifest is a public static
  // file on the web host, not a backend endpoint. `useApi` would attach the JWT
  // (turning this into a CORS preflight that `Allow-Headers: *` does not cover
  // for `Authorization`) and would log the user out on any 401 from that host.
  const manifestUrl = getManifestUrl(useRuntimeConfig().public.otaManifestUrl, getChannel());

  const manifest = await $fetch(withCacheBuster(manifestUrl, Date.now()), {
    timeout: 15000,
  }).catch((error) => {
    console.error('[OTA] Error fetching manifest', error);
    return null;
  });

  if (!isManifestValid(manifest)) {
    console.warn('[OTA] Manifest missing or malformed, ignoring', manifest);
    return null;
  }

  if (!await verifyManifestSignature(manifest, useRuntimeConfig().public.otaPublicKey)) {
    console.error('[OTA] Manifest signature invalid, ignoring', manifest.bundleId);
    return null;
  }

  return manifest;
};

// Decides whether the published bundle should replace the running one.
// Kept pure so the release-blocking rules can be unit tested without a device.
const getUpdateDecision = ({ manifest, nativeVersion, runningBuildDate, blockedBundleIds = [] }) => {
  if (!isManifestValid(manifest)) {
    return { apply: false, reason: 'invalid-manifest' };
  }

  if (blockedBundleIds.includes(manifest.bundleId)) {
    return { apply: false, reason: 'blocked' };
  }

  // A bundle belongs to the binary it was built for — exact match, not a floor.
  // Anything looser lets a bundle published for the previous release land on a
  // device that has just upgraded: the binary is newer, but its baked-in bundle
  // carries an older `buildDate` than an OTA shipped while it sat in App Review,
  // so the date check below would happily reinstate the old JavaScript.
  // Consequence to remember: after an App Store release nothing reaches that
  // binary until a bundle is published for it.
  if (!nativeVersion || manifest.targetNativeVersion !== nativeVersion) {
    return { apply: false, reason: 'built-for-another-binary' };
  }

  // `buildDate` — not the bundle id — is the ordering key. The id says *which*
  // bundle, the build date says *how recent*. Without this a freshly installed
  // binary (whose baked-in bundle is newer than the manifest) would happily
  // downgrade itself to the last published OTA bundle.
  const publishedAt = Date.parse(manifest.buildDate);
  const runningAt = Date.parse(runningBuildDate);

  if (Number.isNaN(publishedAt) || Number.isNaN(runningAt)) {
    return { apply: false, reason: 'unknown-build-date' };
  }

  if (publishedAt <= runningAt) {
    return { apply: false, reason: 'up-to-date' };
  }

  return { apply: true, reason: 'update-available' };
};

const getSeenNativeVersion = () => {
  try {
    return window.localStorage.getItem(NATIVE_VERSION_KEY);
  } catch {
    return null;
  }
};

const setSeenNativeVersion = (nativeVersion) => {
  try {
    window.localStorage.setItem(NATIVE_VERSION_KEY, nativeVersion);
  } catch (error) {
    // Worst case the check below re-runs next launch and drops an already
    // dropped bundle, which is a no-op — not worth failing the startup over.
    console.error('[OTA] Could not persist the native version', error);
  }
};

// An App Store upgrade does not clear the bundle a device is serving. The plugin
// does notice the version change, but its handler only calls `resetConfig()`,
// which resets the Capawesome Cloud app id and nothing else — the persisted
// Capacitor server path still points at the old bundle. So the new binary boots
// straight into the previous release's JavaScript, and `getUpdateDecision()`
// then reports `up-to-date` because that bundle really is newer than the
// manifest. Left alone the device stays there until the next OTA release.
//
// Returns whether the webview is being reloaded, in which case the caller should
// stop: this JS context is about to be torn down.
const resetOnNativeUpgrade = async(nativeVersion, currentBundleId) => {
  const seenVersion = getSeenNativeVersion();

  if (!nativeVersion || seenVersion === nativeVersion) {
    return false;
  }

  // No `seenVersion` is the first launch after this shipped — a baseline to
  // record, not an upgrade. A device already on the App Store bundle
  // (`currentBundleId` null) has nothing to drop either.
  if (!seenVersion || !currentBundleId) {
    setSeenNativeVersion(nativeVersion);

    return false;
  }

  console.warn('[OTA] Native version changed, dropping bundle', currentBundleId);

  // Recorded only once the bundle is actually gone. Writing it first would make
  // a failed reset permanent: the next launch would take the early return above
  // and the device would keep serving the previous release's JavaScript for
  // good, because `getUpdateDecision()` then reports it as up-to-date.
  await LiveUpdate.reset();
  setSeenNativeVersion(nativeVersion);

  // The reloaded bundle is the App Store one, so `currentBundleId` is null next
  // time round and the guard above stops this from looping.
  await LiveUpdate.reload();

  return true;
};

// Notifies the plugin that the bundle booted. Until this resolves, the native
// `readyTimeout` is counting down towards a rollback, so it must run before any
// network I/O — see plugins/ota-update.client.js.
const markReady = async() => {
  const result = await LiveUpdate.ready().catch((error) => {
    console.error('[OTA] Error calling ready()', error);
    return null;
  });

  if (result?.rollback) {
    // The previous bundle failed to boot and was reverted natively. Remember it
    // so the update check below does not immediately fetch it again.
    console.warn('[OTA] Rolled back to default bundle', result);
    blockBundleId(result.previousBundleId);
  }

  return result;
};

const check = async() => {
  const manifest = await fetchManifest();

  if (!manifest) {
    return null;
  }

  const { version: nativeVersion } = await App.getInfo();

  const decision = getUpdateDecision({
    manifest,
    nativeVersion,
    runningBuildDate: useRuntimeConfig().public.buildDate,
    blockedBundleIds: getBlockedBundleIds(),
  });

  if (!decision.apply) {
    console.log('[OTA] No update applied', decision.reason, manifest.bundleId);
    return null;
  }

  // A bundle downloaded during an earlier session only needs activating.
  const { bundleIds } = await LiveUpdate.getBundles();

  if (!bundleIds.includes(manifest.bundleId)) {
    console.log('[OTA] Downloading bundle', manifest.bundleId);

    await LiveUpdate.downloadBundle({
      url: manifest.url,
      bundleId: manifest.bundleId,
      checksum: manifest.checksum,
      signature: manifest.signature,
    });
  }

  await LiveUpdate.setNextBundle({ bundleId: manifest.bundleId });
  console.log('[OTA] Bundle staged for next launch', manifest.bundleId);

  return manifest;
};

export default {
  blockBundleId,
  check,
  DEFAULT_CHANNEL,
  getChannel,
  getManifestSigningInput,
  getManifestUrl,
  getUpdateDecision,
  isManifestValid,
  markReady,
  resetOnNativeUpgrade,
  setChannel,
  verifyManifestSignature,
  withCacheBuster,
};
